/**
 * Pluckonomics — Platform Fees breakdown table (Phase E.2).
 *
 * Reads `GET /api/admin/analytics/fees-breakdown`. Renders one row per
 * fee category:
 *   - Row 1: DERIVED "Platform commission" — total commission_amount_cents
 *     earned on PAID bookings in the MTD window. Light up when ≥1 paid
 *     row exists; otherwise show the orange "Activates with payments" pill.
 *   - Rows 2..N: the admin-configured `platform_fees` rows (Listing /
 *     Advertising / Other). These always render gated with a
 *     "Configured only" note — they're queued for Phase 3.x and don't
 *     pull money today.
 *
 * Empty-state: if `rows` is empty (shouldn't happen because the platform
 * commission row is always present), render a soft placeholder.
 *
 * Item 12 (Feb 2026 launch-blocking batch): the DISPLAY LABEL for the
 * "Platform commission" category is softened to "Platform fee" via
 * `_displayCategory()` below. The backend `category` string is
 * UNCHANGED — the API contract, the tests
 * (`test_pluckonomics_e2_fees_poobucks.py`, etc.), and the calculation
 * logic all still refer to "Platform commission" as the canonical
 * category name. Only the rendered label softens.
 */
import { Card, CardContent } from "@/components/ui/card";

// Item 12 display-label map. Frontend-only. Keys are the exact backend
// `category` values; values are the softened partner-friendly labels.
const _CATEGORY_LABEL_OVERRIDES = {
  "Platform commission": "Platform fee",
};

function _displayCategory(category) {
  if (typeof category !== "string") return category;
  return _CATEGORY_LABEL_OVERRIDES[category] || category;
}

function _formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(cents / 100);
}

function _basisLabel(basis) {
  switch (basis) {
    case "per_booking_pct":  return "% of booking";
    case "per_booking_flat": return "Flat per booking";
    case "monthly_flat":     return "Monthly flat";
    default:                 return basis;
  }
}

function GatedPill({ text }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: "rgba(248, 150, 35, 0.12)", color: "var(--pp-orange)" }}
    >
      {text}
    </span>
  );
}

export default function PlatformFeesTable({ data }) {
  if (!data) {
    return (
      <div className="p-6 text-sm" style={{ color: "var(--pp-text-soft)" }}
           data-testid="pluckonomics-fees-loading">
        Loading platform fees…
      </div>
    );
  }
  const { rows, totals } = data;
  return (
    <Card
      style={{ background: "var(--pp-v3-surface)", borderColor: "var(--pp-border)" }}
      data-testid="pluckonomics-fees-card"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: "var(--pp-text)" }}
                data-testid="pluckonomics-fees-heading">
              Platform fees (MTD)
            </h2>
            <p className="mt-1 text-xs" style={{ color: "var(--pp-text-soft)" }}>
              Platform fee earned on paid bookings + admin-configured fee categories.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-bold uppercase tracking-[0.06em]"
                 style={{ color: "var(--pp-text-soft)" }}>
              Captured (MTD)
            </div>
            <div className="text-2xl font-semibold tracking-tight"
                 style={{ color: totals?.display_state === "stripe_gated"
                            ? "var(--pp-text-soft)" : "var(--pp-text)" }}
                 data-testid="pluckonomics-fees-total">
              {totals?.display_state === "stripe_gated"
                ? "—" : _formatCents(totals?.total_charged_cents)}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto" data-testid="pluckonomics-fees-table-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--pp-border)" }}>
                <th className="py-2 pr-3 text-left text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}>Category</th>
                <th className="py-2 px-3 text-left text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}>Basis</th>
                <th className="py-2 px-3 text-right text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}>Count</th>
                <th className="py-2 pl-3 text-right text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: "var(--pp-text-soft)" }}>Charged (MTD)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isGated = row.display_state === "stripe_gated";
                return (
                  <tr
                    key={`${row.category}-${idx}`}
                    style={{ borderBottom: "1px solid var(--pp-border)" }}
                    data-testid={`pluckonomics-fee-row-${idx}`}
                    data-display-state={row.display_state}
                    data-source={row.source}
                  >
                    <td className="py-3 pr-3">
                      <div className="font-medium" style={{ color: "var(--pp-text)" }}>
                        {_displayCategory(row.category)}
                      </div>
                      {row.source === "configured" && (
                        <div className="mt-1 text-[11px]" style={{ color: "var(--pp-text-soft)" }}>
                          {row.enabled ? "Enabled" : "Disabled"} ·
                          {" "}{row.configured_value != null ? row.configured_value : "—"}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3" style={{ color: "var(--pp-text-soft)" }}>
                      {_basisLabel(row.basis)}
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums"
                        style={{ color: isGated ? "var(--pp-text-soft)" : "var(--pp-text)" }}>
                      {isGated ? "—" : row.count_paid?.toLocaleString("en-US")}
                    </td>
                    <td className="py-3 pl-3 text-right tabular-nums">
                      {isGated ? (
                        <GatedPill text={row.note || "Activates with payments"} />
                      ) : (
                        <span style={{ color: "var(--pp-text)", fontWeight: 600 }}>
                          {_formatCents(row.total_charged_cents)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
